<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher();
  
  let username = '';
  let password = '';
  let isLoading = false;
  let errorMessage = '';
  
  async function handleLogin() {
    if (!username || !password) {
      errorMessage = 'Por favor, introduce usuario y contraseña';
      return;
    }
    
    isLoading = true;
    errorMessage = '';
    
    try {
      const response = await fetch('/auth/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify({
          username,
          password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Login successful - redirect to menu
        window.location.href = '/contigo-menu';
      } else {
        errorMessage = data.error || 'Error de autenticación';
      }
    } catch (error) {
      errorMessage = 'Error de conexión. Inténtalo de nuevo.';
    } finally {
      isLoading = false;
    }
  }
  
  function getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    return token || '';
  }
  
  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      handleLogin();
    }
  }
</script>

<svelte:head>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous">
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+HK:wght@400;500;600;700&display=swap" rel="stylesheet">
</svelte:head>

<div class="login-container">
  <div class="login-card">
    <div class="header-section">
      <div class="logo-section">
        <img src="/static/icons/contigo logo.svg" alt="Contigo Logo" class="contigo-logo" />
      </div>
      <h1>Programa Contigo</h1>
      <p class="subtitle">Acceso al sistema</p>
    </div>
    
    <form class="login-form" on:submit|preventDefault={handleLogin}>
      <div class="form-group">
        <label for="username">Usuario</label>
        <input
          id="username"
          type="text"
          bind:value={username}
          on:keypress={handleKeyPress}
          disabled={isLoading}
          placeholder="Introduce tu usuario"
          required
        />
      </div>
      
      <div class="form-group">
        <label for="password">Contraseña</label>
        <input
          id="password"
          type="password"
          bind:value={password}
          on:keypress={handleKeyPress}
          disabled={isLoading}
          placeholder="Introduce tu contraseña"
          required
        />
      </div>
      
      {#if errorMessage}
        <div class="error-message">
          {errorMessage}
        </div>
      {/if}
      
      <button 
        type="submit" 
        class="login-button"
        disabled={isLoading || !username || !password}
      >
        {#if isLoading}
          Iniciando sesión...
        {:else}
          Iniciar sesión
        {/if}
      </button>
    </form>
  </div>
</div>

<style>
  :global(:root) {
    /* Using the same color palette as the dashboard */
    --color-primary: #368f3f;
    --color-primary-dark: #007932;
    --color-primary-light: #E8F5E8;
    --color-surface: #FFFEF7;
    --color-background: #FDF6E3;
    --color-text: #2e2925;
    --color-text-light: #6B6B6B;
    --color-border: #E8DCC6;
    --color-success: #368f3f;
    --color-warning: #DAA520;
    --color-error: #CD853F;
    --color-accent: #DAA520;

    /* Typography */
    --font-family-base: 'Noto Sans HK', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    --font-weight-normal: 400;
    --font-weight-medium: 500;
    --font-weight-semibold: 600;
    --font-weight-bold: 700;
    --font-size-sm: 0.875rem;
    --font-size-base: 1.0625rem;
    --font-size-lg: 1.125rem;
    --font-size-xl: 1.25rem;
    --font-size-2xl: 1.5rem;
    --line-height-normal: 1.5;

    /* Spacing */
    --spacing-sm: 0.5rem;
    --spacing-md: 0.875rem;
    --spacing-lg: 1.25rem;
    --spacing-xl: 1.75rem;
    --spacing-2xl: 2.5rem;

    /* Shadows */
    --shadow-md: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08);
    --shadow-lg: 0 8px 16px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1);

    /* Border radius */
    --border-radius-md: 0.5rem;
    --border-radius-lg: 0.75rem;
    --border-radius-xl: 1rem;
  }

  :global(body) {
    font-family: var(--font-family-base);
    font-weight: var(--font-weight-normal);
    background-color: var(--color-background);
    color: var(--color-text);
    line-height: var(--line-height-normal);
    font-size: var(--font-size-base);
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-lg);
    background: linear-gradient(135deg, var(--color-background) 0%, var(--color-primary-light) 100%);
  }

  .login-card {
    background: var(--color-surface);
    border-radius: var(--border-radius-xl);
    padding: var(--spacing-2xl);
    box-shadow: var(--shadow-lg);
    border: 2px solid var(--color-border);
    width: 100%;
    max-width: 420px;
  }

  .header-section {
    text-align: center;
    margin-bottom: var(--spacing-2xl);
  }

  .logo-section {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-lg);
  }

  .contigo-logo {
    height: 48px;
    width: auto;
  }

  .header-section h1 {
    margin: 0 0 var(--spacing-sm) 0;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-primary);
    letter-spacing: -0.025em;
  }

  .subtitle {
    margin: 0;
    font-size: var(--font-size-base);
    color: var(--color-text-light);
    font-weight: var(--font-weight-medium);
  }

  .login-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .form-group label {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text);
  }

  .form-group input {
    padding: var(--spacing-md) var(--spacing-lg);
    border: 2px solid var(--color-border);
    border-radius: var(--border-radius-md);
    font-size: var(--font-size-base);
    font-family: var(--font-family-base);
    background: var(--color-surface);
    color: var(--color-text);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .form-group input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(54, 143, 63, 0.1);
  }

  .form-group input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .form-group input::placeholder {
    color: var(--color-text-light);
  }

  .error-message {
    background: rgba(205, 133, 63, 0.1);
    color: var(--color-error);
    padding: var(--spacing-md);
    border-radius: var(--border-radius-md);
    border: 1px solid rgba(205, 133, 63, 0.3);
    font-size: var(--font-size-sm);
    text-align: center;
  }

  .login-button {
    background: var(--color-primary);
    color: white;
    border: none;
    padding: var(--spacing-lg) var(--spacing-xl);
    border-radius: var(--border-radius-md);
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    font-family: var(--font-family-base);
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    margin-top: var(--spacing-md);
  }

  .login-button:hover:not(:disabled) {
    background: var(--color-primary-dark);
    transform: translateY(-1px);
  }

  .login-button:active:not(:disabled) {
    transform: translateY(0);
  }

  .login-button:disabled {
    background: var(--color-text-light);
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    .login-container {
      padding: var(--spacing-md);
    }
    
    .login-card {
      padding: var(--spacing-xl);
    }
    
    .logo-section {
      flex-direction: column;
      gap: var(--spacing-sm);
    }
    
    .contigo-logo {
      height: 40px;
    }
  }
</style> 